import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Contact, Phone, Plus, X } from 'lucide-react';
import { useClientsStore } from '../store/clientsStore';
import { errorDetailText } from '../services/api';
import { waLink } from '../components/practice/format';
import { Button, EmptyState } from '../components/ui';
import { WhatsAppIcon } from './landing/data';

const INPUT_CLS =
  'w-full text-sm border border-legal-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-accent';

/** Client book: everyone you act for, with one-tap call / WhatsApp. */
export function ClientsPage() {
  const { clients, lastFetched, isFetching, fetchClients, createClient } = useClientsStore();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: '',
    phone: '',
    whatsapp: '',
    email: '',
    address: '',
    notes: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const setField = (key: keyof typeof form, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || isSaving) return;
    setIsSaving(true);
    setError('');
    try {
      await createClient({
        name: form.name.trim(),
        phone: form.phone.trim() || undefined,
        whatsapp: form.whatsapp.trim() || undefined,
        email: form.email.trim() || undefined,
        address: form.address.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      setForm({ name: '', phone: '', whatsapp: '', email: '', address: '', notes: '' });
      setShowForm(false);
      fetchClients();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not save the client.'));
    } finally {
      setIsSaving(false);
    }
  };

  const isFirstLoad = clients.length === 0 && (isFetching || lastFetched === null);

  return (
    <div className="p-2 sm:p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-display text-3xl font-medium text-legal-900">Clients</h1>
          <p className="text-legal-500 mt-1 text-[15px]">
            Your client book — every case they have with you, one tap away.
          </p>
        </div>
        <Button size="md" onClick={() => setShowForm((v) => !v)} className="shrink-0">
          {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          <span className="hidden sm:inline">{showForm ? 'Cancel' : 'New client'}</span>
          <span className="sm:hidden">{showForm ? 'Cancel' : 'New'}</span>
        </Button>
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="bg-white rounded-card border border-legal-200 p-4 mb-6 space-y-3 animate-slide-up"
        >
          <input
            autoFocus
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            maxLength={255}
            placeholder="Client name"
            className={INPUT_CLS}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setField('phone', e.target.value)}
              maxLength={20}
              placeholder="Phone"
              className={INPUT_CLS}
            />
            <input
              type="tel"
              value={form.whatsapp}
              onChange={(e) => setField('whatsapp', e.target.value)}
              maxLength={20}
              placeholder="WhatsApp (if different)"
              className={INPUT_CLS}
            />
            <input
              type="email"
              value={form.email}
              onChange={(e) => setField('email', e.target.value)}
              maxLength={255}
              placeholder="Email (optional)"
              className={INPUT_CLS}
            />
            <input
              value={form.address}
              onChange={(e) => setField('address', e.target.value)}
              maxLength={500}
              placeholder="Address (optional)"
              className={INPUT_CLS}
            />
          </div>
          <textarea
            value={form.notes}
            onChange={(e) => setField('notes', e.target.value)}
            maxLength={5000}
            rows={2}
            placeholder="Notes (optional)"
            className={`${INPUT_CLS} resize-none`}
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" size="md" isLoading={isSaving} disabled={!form.name.trim()}>
            Save client
          </Button>
        </form>
      )}

      {isFirstLoad ? (
        <div className="animate-pulse space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 bg-legal-100 rounded-card" />
          ))}
        </div>
      ) : clients.length === 0 ? (
        <EmptyState
          icon={Contact}
          title="No clients yet"
          message="Add a client to link their cases and reach them in one tap."
          action={
            <Button size="md" onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4" /> New client
            </Button>
          }
        />
      ) : (
        <div className="space-y-3 stagger-children">
          {clients.map((client) => (
            <div
              key={client.id}
              className="flex items-center gap-3 bg-white rounded-card border border-legal-200 p-4 hover:border-legal-300 card-lift"
            >
              <Link
                to={`/app/clients/${client.id}`}
                state={{ client }}
                className="min-w-0 flex-1"
              >
                <p className="font-medium text-legal-900 truncate">{client.name}</p>
                <p className="text-xs text-legal-400 mt-0.5 truncate">
                  {[client.phone, `${client.cases_count} case${client.cases_count === 1 ? '' : 's'}`]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </Link>
              {client.phone && (
                <Button
                  variant="secondary"
                  size="md"
                  href={`tel:${client.phone}`}
                  data-track="call-client"
                  aria-label={`Call ${client.name}`}
                  className="shrink-0 px-3"
                >
                  <Phone className="h-4 w-4" />
                </Button>
              )}
              {(client.whatsapp || client.phone) && (
                <Button
                  variant="secondary"
                  size="md"
                  href={waLink(client.whatsapp || client.phone || '')}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track="call-client"
                  aria-label={`WhatsApp ${client.name}`}
                  className="shrink-0 px-3 text-[#25D366] hover:text-[#25D366]"
                >
                  <WhatsAppIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
